import { Send, ShieldCheck, ShieldQuestion } from '@orgus/ui/icons';
import { useState } from 'react';
import type { Session } from '@orgus/protocol';
import { SESSION_MESSAGE_MAX_CHARS } from '@orgus/protocol';
import { Badge, Button, Composer, Kbd, Tooltip, toUiError } from '@orgus/ui';
import { useMachine } from '../../lib/store.ts';
import { useCan } from '../auth/useMe.ts';
import { DraftFiles, useDraftFiles } from '../attachments/DraftFiles.tsx';
import { sendSessionMessage, useControlState } from './live.ts';
import { controlMarkFor } from './model.ts';
import { speakerOf } from '../adapters/state.ts';
import { replyBoxId, useReplyFill } from '../session-detail/replyDraft.ts';

/**
 * "Controlled" on a session of a machine where control is on; "Not controlled yet" says why.
 * compact: for list rows (inside a pressable row, so no focusable tooltip; the reason is the title).
 */
export function ControlMark({ session, compact }: { session: Pick<Session, 'id' | 'machineId' | 'status' | 'startedAt'>; compact?: boolean }) {
  const mark = controlMarkFor(useControlState(), session);
  if (mark.kind === 'none') return null;
  if (compact) {
    return mark.kind === 'controlled' ? (
      <Badge tone="passed" variant="outline" className="hidden sm:inline-flex" icon={<ShieldCheck aria-hidden />} title="Approvals, budget stops, policy and messages from Orgus reach this session.">
        Controlled
      </Badge>
    ) : (
      <Badge tone="waiting" variant="outline" className="hidden sm:inline-flex" icon={<ShieldQuestion aria-hidden />} title={mark.text}>
        Not controlled yet
      </Badge>
    );
  }
  if (mark.kind === 'controlled') {
    return (
      <Tooltip content={mark.launched ? 'Started by Orgus, with its own hooks.' : 'Approvals, budget stops, policy and messages from Orgus reach this session.'}>
        <span tabIndex={0}>
          <Badge tone="passed" icon={<ShieldCheck aria-hidden />}>
            Controlled
          </Badge>
        </span>
      </Tooltip>
    );
  }
  return (
    <Tooltip content={mark.text}>
      <span tabIndex={0}>
        <Badge tone="waiting" variant="outline" icon={<ShieldQuestion aria-hidden />}>
          Not controlled yet
        </Badge>
      </span>
    </Tooltip>
  );
}

/**
 * A message into a live session on a controlled machine, through Claude Code's own channel,
 * with screenshots and files (copied to the machine; the message lists their paths).
 * pasteAnywhere: take screenshots pasted anywhere on the page (the phone's session view).
 */
export function SessionMessage({ session, pasteAnywhere = false, card = false }: { session: Session; pasteAnywhere?: boolean; card?: boolean }) {
  const state = useControlState();
  const machine = useMachine(session.machineId);
  const canSend = useCan('operator');
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; text: string } | null>(null);
  const drafts = useDraftFiles();
  // A quick reply from the waiting card lands here, focused and not sent (ADR 0056).
  useReplyFill(session.id, setText);
  const m = state?.machines[session.machineId];
  if (!m?.installed || session.status === 'ended') return null;
  const online = !!machine?.online;
  const blocked = !canSend ? 'Viewers cannot message sessions. Ask an operator.' : !online ? `${machine?.name ?? 'The machine'} is offline.` : null;
  const trimmed = text.trim();
  const ready = !!trimmed || drafts.ids.length > 0;

  const send = async () => {
    setBusy(true);
    setResult(null);
    try {
      const r = await sendSessionMessage(session.machineId, session.id, trimmed, drafts.ids);
      setResult({ ok: r.ok, text: r.message });
      if (r.ok) {
        setText('');
        drafts.clear();
      }
    } catch (e) {
      const err = toUiError(e);
      setResult({ ok: false, text: `${err.what} ${err.why} ${err.next}`.trim() });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className={card ? 'flex flex-col gap-2 rounded-card border border-border bg-surface p-4 hairline' : 'flex flex-col gap-2'} aria-labelledby={`msg-${session.id}`}>
      <h3 id={`msg-${session.id}`} className="text-caption text-fg-subtle">
        Message
      </h3>
      <Composer
        id={replyBoxId(session.id)}
        value={text}
        maxLength={SESSION_MESSAGE_MAX_CHARS}
        placeholder={`Message ${speakerOf(session)}`}
        disabled={!!blocked || busy}
        onChange={(e) => setText(e.target.value)}
        onSubmit={() => ready && !blocked && !busy && void send()}
        aria-label="Message to this session"
        tools={<DraftFiles inline drafts={drafts} disabled={!!blocked || busy} pasteAnywhere={pasteAnywhere} hint="Files are copied to the session's machine." />}
        hint={<Kbd keys={['mod', 'enter']} />}
        send={
          <Tooltip content={blocked ?? "Sent as the session's next prompt"}>
            <span tabIndex={blocked ? 0 : -1}>
              <Button size="sm" variant="primary" icon={<Send />} loading={busy} disabled={!!blocked || !ready} onClick={() => void send()}>
                Send
              </Button>
            </span>
          </Tooltip>
        }
        note={
          result ? (
            <span role="status" className={result.ok ? 'text-status-success' : 'text-status-danger'}>
              {result.text}
            </span>
          ) : (
            blocked
          )
        }
      />
    </section>
  );
}
